import axios from "@axios";

const DEFAULT_STATE = {
  topic: "",
  topicIsTitle: false,
  sources: [],
  scrapedSources: {},
  sites: [],
  selectedSite: null,
  models: [],
  selectedModel: {
    name: "gpt-4o",
    temperature: 0.4,
    top_p: 0.9,
    verbosity: "medium",
    reasoning_effort: "medium",
  },
  prompts: [],
  promptResults: {},
  isLoading: false,
  error: null,
  allowAutoScrape: false,
  // Mode image (jamais un nom de modèle) : "generate" | "none" | "image_url" | "image_id".
  // Le modèle, la taille et le prompt viennent du binding LLPR AIF_IMAGE du site.
  imageHandler: "generate",
};

export default {
  namespaced: true,

  state: {
    ...DEFAULT_STATE,
  },

  getters: {
    getTopic: (state) => state.topic,
    getTopicIsTitle: (state) => state.topicIsTitle,
    getSources: (state) => state.sources,
    getScrapedSources: (state) => state.scrapedSources,
    getSites: (state) => state.sites,
    getSelectedSite: (state) => state.selectedSite,
    getModels: (state) => state.models,
    getSelectedModel: (state) => state.selectedModel,
    getPrompts: (state) => state.prompts,
    getPromptResults: (state) => state.promptResults,
    isLoading: (state) => state.isLoading,
    getError: (state) => state.error,
    getSourcesText: (state) => state.scrapedSources?.combined_text || "",
    getAllowAutoScrape: (state) => state.allowAutoScrape,
    getImageHandler: (state) => state.imageHandler,
  },

  mutations: {
    INIT_STATE(state) {
      for (const [key, value] of Object.entries(DEFAULT_STATE)) {
        state[key] = value;
      }
    },

    SET_ALLOW_AUTO_SCRAPE(state, value) {
      state.allowAutoScrape = value;
    },

    SET_IMAGE_HANDLER(state, value) {
      state.imageHandler = value;
    },

    SET_TOPIC(state, topic) {
      state.topic = topic;
    },

    SET_TOPIC_IS_TITLE(state, value) {
      state.topicIsTitle = value;
    },

    SET_SOURCES(state, sources) {
      state.sources = sources;
    },

    ADD_SOURCE(state, source) {
      state.sources.push(source);
    },

    REMOVE_SOURCE(state, index) {
      state.sources.splice(index, 1);
    },

    SET_SCRAPED_SOURCES(state, data) {
      state.scrapedSources = data;
    },

    SET_SITES(state, sites) {
      state.sites = sites;
    },

    SET_SELECTED_SITE(state, site) {
      state.selectedSite = site;
    },

    SET_MODELS(state, models) {
      state.models = models;
    },

    SET_SELECTED_MODEL(state, model) {
      state.selectedModel = model;
    },

    UPDATE_MODEL_PARAMS(state, params) {
      state.selectedModel = { ...state.selectedModel, ...params };
    },

    SET_PROMPTS(state, prompts) {
      state.prompts = prompts;
    },

    SET_PROMPT_RESULT(state, { promptType, result }) {
      state.promptResults = {
        ...state.promptResults,
        [promptType]: result,
      };
    },

    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    CLEAR_RESULTS(state) {
      state.promptResults = {};
    },
  },

  actions: {
    initState({ commit }) {
      commit("INIT_STATE");
    },

    updateAllowAutoScrape({ commit }, value) {
      commit("SET_ALLOW_AUTO_SCRAPE", value);
    },

    updateImageHandler({ commit }, value) {
      commit("SET_IMAGE_HANDLER", value);
    },

    async fetchSiteTemplateConfig({ commit }, siteId) {
      //if (!siteId) return null;
      if (!siteId) siteId = 0;
      try {
        const response = await axios.get(
          `/api/ai-factory/default-prompt-template-by-site-id/${siteId}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching site template config:", error);
        // On ne bloque pas l'app si la config échoue, on retourne null
        return null;
      }
    },

    async addSourceAuto({ commit, state }, siteId = null) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);

      try {
        const payload = {
          topic: state.topic,
          process_type: "via_angles",
        };

        // Include site_id if provided (parameter takes precedence over store)
        const finalSiteId =
          siteId !== null && siteId !== undefined ? siteId : state.selectedSite;

        if (finalSiteId) {
          console.log("Using site ID:", finalSiteId);
          payload.site_id = finalSiteId;
        }

        const response = await axios.post(
          "/api/ai-factory/sources-auto-by-topic",
          payload
        );

        // L’API renvoie un objet { topics: { [topic]: [urls] }, error: null }
        const urls = response.data.topics?.[state.topic] || [];

        // On garde uniquement les sources qui ne sont pas vides
        const cleanedSources = state.sources.filter((s) => {
          if (typeof s === "string") {
            return s.trim() !== "";
          }
          const url = (s.url || "").trim();
          const content = (s.content || "").trim();
          return url !== "" || content !== "";
        });

        // Fusionne avec les nouvelles sources
        const updatedSources = [
          ...cleanedSources,
          ...urls.map((url) => ({ url, content: "" })),
        ];

        commit("SET_SOURCES", updatedSources);
        return urls;
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error fetching auto sources:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchSites({ commit }) {
      try {
        const response = await axios.get("/api/ai-factory/sites");
        commit("SET_SITES", response.data.sites);
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error fetching sites:", error);
      }
    },

    async fetchConnectors({ commit }, params = {}) {
      try {
        // L'appel API est encapsulé ici
        const response = await axios.get("/api/connectors/", {
          params: {
            api_allowed: true, // On ne veut que les sites utilisables
            limit: 0, // Aucune limite, pour tout récupérer
            ...params,
          },
        });
        return response.data; // Retourne { total, items }
      } catch (error) {
        console.error("Erreur API [fetchConnectors]:", error);
        // Propage l'erreur pour que le composant puisse la gérer
        throw error;
      }
    },

    async fetchModels({ commit }) {
      try {
        const response = await axios.get("/api/ai-factory/models");
        commit("SET_MODELS", response.data.models);
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error fetching models:", error);
      }
    },

    async fetchPrompts({ commit }) {
      try {
        const response = await axios.get("/api/ai-factory/prompts");
        commit("SET_PROMPTS", response.data.prompts);
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error fetching prompts:", error);
      }
    },

    async scrapeSources({ commit, state }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);

      try {
        const sourceUrls = state.sources
          .map((s) => (typeof s === "string" ? s : s.url))
          .filter((url) => url && url.trim());

        const response = await axios.post(
          "/api/ai-factory/scrape-sources",
          {
            topic: state.topic,
            sources: sourceUrls,
          }
        );

        commit("SET_SCRAPED_SOURCES", response.data);
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error scraping sources:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async scrapeSingleSource({ commit, state }, { index, url }) {
      commit("SET_ERROR", null);

      try {
        const response = await axios.post(
          "/api/ai-factory/scrape-sources",
          {
            topic: state.topic,
            sources: [url],
          }
        );

        if (response.data.sources && response.data.sources.length > 0) {
          const scrapedSource = response.data.sources[0];
          const updatedSources = [...state.sources];
          const current = updatedSources[index];
          const base =
            typeof current === "string"
              ? { url: current || scrapedSource.url, content: "" }
              : { ...current };

          if (typeof scrapedSource.content === "string") {
            updatedSources[index] = {
              ...base,
              url: base.url || scrapedSource.url,
              content: scrapedSource.content || "",
              scrapeError: null,
              scrapeStatus: null,
            };
          } else if (scrapedSource.error) {
            updatedSources[index] = {
              ...base,
              url: base.url || scrapedSource.url,
              scrapeError: scrapedSource.error,
              scrapeStatus: scrapedSource.status || null,
            };
          }

          commit("SET_SOURCES", updatedSources);
        }

        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error scraping single source:", error);
        throw error;
      }
    },

    updateTopic({ commit }, topic) {
      commit("SET_TOPIC", topic);
    },

    updateTopicIsTitle({ commit }, value) {
      commit("SET_TOPIC_IS_TITLE", value);
    },

    updateSources({ commit }, sources) {
      commit("SET_SOURCES", sources);
    },

    addSource({ commit }, source) {
      commit("ADD_SOURCE", source);
    },

    removeSource({ commit }, index) {
      commit("REMOVE_SOURCE", index);
    },

    updateSelectedSite({ commit }, site) {
      commit("SET_SELECTED_SITE", site);
    },

    updateModelParams({ commit }, params) {
      commit("UPDATE_MODEL_PARAMS", params);
    },

    clearResults({ commit }) {
      commit("CLEAR_RESULTS");
    },

    async sendToPostGenerationQueue({ commit }, payload) {
      // On reçoit le payload complet
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        // 1. Valider la présence du topic et de l'utilisateur
        if (!payload.topic || !payload.topic.trim()) {
          throw new Error("Le topic est manquant.");
        }
        if (!payload.userEmail) {
          throw new Error("Utilisateur non identifié : userEmail manquant.");
        }

        // 2. Répliquer la logique de validation des sources du composant
        const hasSourceUrl = payload.sources?.some((s) => s?.url?.trim());
        const hasSourceContent = payload.sources?.some((s) =>
          s?.content?.trim()
        );

        // Déterminer si la condition des sources est remplie, selon le mode
        const sourceRequirementMet = payload.allowAutoScrape
          ? hasSourceUrl
          : hasSourceContent;

        if (!sourceRequirementMet) {
          const errorMsg = payload.allowAutoScrape
            ? "Au moins une source avec une URL valide est requise."
            : "Au moins une source avec du contenu valide est requise.";
          throw new Error(errorMsg);
        }

        const response = await axios.post(
          "/api/ai-factory/add-to-post-generation-queue",
          {
            user_email: payload.userEmail,
            site_id: payload.siteId,
            topic: payload.topic,
            topic_is_title: payload.topicIsTitle,
            aif_custom_prompt_template_id: payload.aifCustomPromptTemplateId,
            sources: payload.sources,
            status: payload.status,
            //image_handler: payload.skipImage ? null : "openai",
            image_handler: payload.imageHandler,
            scheduled: payload.scheduled,
            rmr_origin_page_id: payload.rmrOriginPageId,
            forced_category_id: payload.forcedCategoryId || null,
          }
        );
        return response.data;
      } catch (error) {
        commit("SET_ERROR", error.message);
        console.error("Error adding to post generation queue:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
  },
};
